import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import FormControl from '@mui/material/FormControl';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { NotificationManager } from "react-notifications";
import getAllTeamByOrgId from "../../apis/teambyOrganizationId/getAllTeamByOrgId"; // teams payload already contains recruiters


const RecruiterReassignmentDialog = ({
    open,
    onClose,
    onSave,
    userType,
    isUserDataUpdating,
    reassignRecruiterList,
    reassignTeamList,
    reassignToRecruiterList,
    reAssignmentObj,
    fromReassignmentChangeHandler,
    newRecruiterReassignmentChangeHandler,
    reassignmentTeamChangeHandler,
    userRecruitmentpartnerId,
    selectedTeam,
    source,
    requisitionDetailId
}) => {
    
    const [teamOptions, setTeamOptions] = useState(reassignTeamList || []);
    const [recruiterOptions, setRecruiterOptions] = useState(reassignToRecruiterList || []);
    const [teamLoading, setTeamLoading] = useState(false);
    const [recruiterLoading, setRecruiterLoading] = useState(false);

    const currentTeamId = (userType === "Team Leader" || userType === "Recruiter") ? selectedTeam?._id : reAssignmentObj?.reassignTeam?._id;

    useEffect(() => {
        if (currentTeamId) {
            fetchRecruitersLocal();
        } else {
            setRecruiterOptions([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTeamId]);

   const fetchTeamsLocal = async () => {
    if (!userRecruitmentpartnerId) return;

    setTeamLoading(true);

    try {
        // Base payload
        const payload = {
            recruitmentpartnerId: userRecruitmentpartnerId,
            isActive: true
        };

        // Conditionally add requisitionDetailId
        if (source === "Activity" && requisitionDetailId) {
            payload.requisitionDetailId = requisitionDetailId;
        }

        const response = await getAllTeamByOrgId(payload);

        if (response.status === 200) {
            setTeamOptions(response.data.data || []);
        } else {
            NotificationManager.error("Error in fetching team options", "Error", 10000);
        }

    } catch (error) {
        NotificationManager.error("Error in fetching team options", "Error", 10000);
    } finally {
        setTeamLoading(false);
    }
};

    // populate recruiters from currently selected team object
    const fetchRecruitersLocal = () => {
        setRecruiterLoading(true);
        let teamObj;
        if (userType === "Team Leader" || userType === "Recruiter") {
            teamObj = selectedTeam;
        } else {
            teamObj = teamOptions.find(t => t._id === reAssignmentObj?.reassignTeam?._id);
        }
        let recruiters = teamObj?.recruiter || [];
        // filter out the currently assigned recruiter to avoid showing in "Reassign To"
        const assignedEmail = reAssignmentObj?.reassginedFrom?.email;
        if (assignedEmail) {
            recruiters = recruiters.filter(r => r.email !== assignedEmail);
            // also clear selected reassginedTo if it matches removed recruiter
            if (reAssignmentObj?.reassginedTo?.email === assignedEmail) {
                setRecruiterOptions(recruiters);
                setRecruiterLoading(false);
                return;
            }
        }
        setRecruiterOptions(recruiters);
        setRecruiterLoading(false);
    }

    // set recruiters based on team id from loaded teamOptions
    const fetchRecruitersForTeam = (teamId) => {
        setRecruiterLoading(true);
        const teamObj = teamOptions.find(t => t._id === teamId);
        let recruiters = teamObj?.recruiter || [];
        const assignedEmail = reAssignmentObj?.reassginedFrom?.email;
        if (assignedEmail) {
            recruiters = recruiters.filter(r => r.email !== assignedEmail);
            if (reAssignmentObj?.reassginedTo?.email === assignedEmail) {
                // clear if previously selected
                reAssignmentObj.reassginedTo = {};
            }
        }
        setRecruiterOptions(recruiters);
        setRecruiterLoading(false);
    }
    return (
        <Dialog
            onClose={onClose}
            fullWidth
            maxWidth="md"
            open={open}
            className="dialogBox_reassignRecruiter"
        >
            <DialogTitle className="header">
                <div className='header_top'>
                    {source === "Activity" ? "Candidate Ownership Transfer" : "Recruiter Reassignment"}
                </div>
            </DialogTitle>

            <DialogContent className='reassignRecruiterContainer'>

                <Typography className='label_recruiterReassignment'>
                    Assigned Recruiter *
                </Typography>

                <FormControl fullWidth>
                    <Autocomplete
                        options={reassignRecruiterList}
                        getOptionLabel={(option) => option?.email || ""}
                        disableClearable
                        value={reAssignmentObj.reassginedFrom}
                        disabled={source === "Activity"}
                        onChange={(e, value) => fromReassignmentChangeHandler(value)}
                        renderInput={(params) =>
                            <TextField {...params} required placeholder="Select Recruiter" />
                        }
                    />
                </FormControl>

                {userType !== "Team Leader" && userType !== "Recruiter" && (
                    <>
                        <Typography className='label_recruiterReassignment'>
                            Choose Team *
                        </Typography>

                        <FormControl fullWidth>
                            <Autocomplete
                                options={teamOptions}
                                getOptionLabel={(option) => option?.teamName || ""}
                                loading={teamLoading}
                                onFocus={() => teamOptions.length === 0 && fetchTeamsLocal()}
                                onChange={(e, value) => {
                                    reassignmentTeamChangeHandler(value);
                                    if (value && value._id) {
                                        fetchRecruitersForTeam(value._id);
                                    } else {
                                        setRecruiterOptions([]);
                                    }
                                }
                                }
                                value={reAssignmentObj.reassignTeam}
                                disabled={!reAssignmentObj.reassginedFrom?.id}
                                renderInput={(params) =>
                                    <TextField {...params} required placeholder="Select Team" />
                                }
                            />
                        </FormControl>
                    </>
                )}

                <Typography className='label_recruiterReassignment'>
                    Reassign To *
                </Typography>

                <FormControl fullWidth>
                    <Autocomplete
                        options={recruiterOptions}
                        getOptionLabel={(option) => option?.email || ""}
                        loading={recruiterLoading}
                        onFocus={() => {
                            // always update recruiter list from current team object when user focuses
                            fetchRecruitersLocal();
                        }}
                        onChange={(e, value) =>
                            newRecruiterReassignmentChangeHandler(value)
                        }
                        value={reAssignmentObj.reassginedTo}
                        disabled={!reAssignmentObj.reassginedFrom?.id}
                        renderInput={(params) =>
                            <TextField {...params} required placeholder="Select Recruiter" />
                        }
                    />
                </FormControl>
                {source === "Activity" ? "" :
                <Typography className='note_recruiterReassignment'>
                    *<b>Note:</b> Candidates those are not in "STARTED" stage will be reassigned.
                </Typography>
                }

            </DialogContent>

            <DialogActions className="dialog-actionbutton-assignTeamLeader">
                <button
                    className="cancel-button-assignTeamLeader"
                    onClick={onClose}
                >
                    Cancel
                </button>

                {userType !== "Recruiter" && (
                    <button
                        className={
                            isUserDataUpdating
                                ? 'updateButton-assignTeamLeader_disabled'
                                : 'updateButton-assignTeamLeader'
                        }
                        onClick={onSave}
                        disabled={isUserDataUpdating}
                    >
                        <span>Save</span>
                        {isUserDataUpdating &&
                            <CircularProgress className="circularProgress_updateButton" />
                        }
                    </button>
                )}
            </DialogActions>

        </Dialog>
    );
};

export default RecruiterReassignmentDialog;